/**
 * PHONE-IN-LEAD-CAPTURE-V1 -- the call-centre agent's "log a phone enquiry"
 * door (SEPT15-REACHABILITY-PASS-V1 activity 3: "a manual 'log a phone-in
 * enquiry' capture form for the agent").
 *
 * An agent takes a live phone enquiry with no digital trace and records it:
 * name, mobile, requirement bucket, clinic. The lead lands in the SAME
 * table, through the SAME writer, as a web lead:
 *
 *   - THE ONE WRITER: lib/lead-capture-handler.ts::captureLead, reached
 *     through the EXISTING session-authed operator door
 *     POST /api/leads/enquiries (api/routes/lead-enquiry.ts, R-CD-B1) --
 *     no second lead-insert path and no second staff-capture route. The
 *     agent's identity comes from the session (auditPrincipal server-side),
 *     never from this form.
 *   - SOURCE IS FIXED: 'offline' -- the phone-in channel token already in
 *     the LIVE leads.source_normalized CHECK (schema_master.sql; migration
 *     20260513_2 APPLIED per the applied-state ledger). The agent never
 *     picks a source here; the form states it. The unapplied tokens
 *     (whatsapp / webchat, UNAPPLIED_LEAD_SOURCE_TOKENS) are structurally
 *     unreachable from this form.
 *   - SEARCH-FIRST (R-CD-R1, STANDING LAW): every person-record creation
 *     begins with a phone search; creation is exclusively the NOT-FOUND
 *     outcome (or an explicit "None of these"). This form reuses the SAME
 *     evidence machine + copy as the leads-workspace door
 *     (lead-enquiry-model.ts) and the SAME patient-master search read --
 *     never a second search engine, never an unguarded create.
 *   - UI: registered primitives only (ModalShell, AcButton) + the wizard
 *     field grammar (ac-wizard__field / __label / __field-error) -- no
 *     hand-built input styling.
 *   - ERRORS: client pre-validation is FIELD-SCOPED (the arrival-wizard
 *     errorFor/FieldError idiom); a server 4xx message passes through
 *     VERBATIM (LES-009). The server route remains the security boundary
 *     (org scope, clinic fence, frozen write audience, vocabulary).
 *   - SUCCESS: the agent is told the lead was captured, offered the lead's
 *     canonical page, and "Log another call" clears the form with a FRESH
 *     requestId (a reused id would idempotently dedupe the next capture
 *     onto the first lead -- LESS-AC-02 is per-submission, not per-modal).
 *
 * PII: this module renders what the operator typed and what the fenced
 * search wire returned; it logs nothing and never widens any read.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  createOperatorEnquiry,
  fetchOperatorEnquiryConfig,
  fetchPatientMasterSearch,
  type OperatorEnquiryConfigWire,
  type OperatorEnquiryCreatedWire,
} from '@/dashboard/api';
import { AcButton, AcInput, ModalShell, SelectorBar, type VisualVariant } from '@/dashboard/ui';
import {
  RecordCard,
  RequiredMark,
  type SearchResultCardView,
} from '@/dashboard/patients/RegisterPatientSteps';
import {
  patientRecordHref,
  type EnquiryGlanceCard,
  type PatientSearchResponse,
} from '@/dashboard/patients/patient-master-view';
import { humanizeSource } from '@/walkin/ExistingLeadMatches';
import {
  LEAD_NONE_OF_THESE,
  LEAD_OPEN_ENQUIRY_ACTION,
  LEAD_PHONE_FIRST_HINT,
  LEAD_SEARCH_BUTTON,
  LEAD_SEARCH_UNAVAILABLE,
  leadEnquiryEvidence,
  leadSearchNoRecordLine,
} from '@/dashboard/leads/lead-enquiry-model';

// ---- Copy (ONE home; the page door + tests import from here) ---------------

export const PHONE_IN_DOOR_LABEL = 'Log phone enquiry';
export const PHONE_IN_TITLE = 'Log a phone enquiry';
export const PHONE_IN_SEARCH_PLACEHOLDER = 'Caller mobile number';
export const PHONE_IN_CREATE_TITLE = 'New phone enquiry';
export const PHONE_IN_SAVE_ACTION = 'Save enquiry';
export const PHONE_IN_SUCCESS = 'Lead captured.';
export const PHONE_IN_ANOTHER_ACTION = 'Log another call';
export const PHONE_IN_OPEN_LEAD_ACTION = 'Open lead';
/** The fixed-channel statement (the agent never picks a source here). */
export const PHONE_IN_SOURCE_LINE = `Source is recorded as ${humanizeSource(
  'offline',
)} - a phone enquiry taken by the desk.`;
/** Honest state when the org config narrowed the offered channels past
 *  'offline' -- the save door closes instead of failing at submit. */
export const PHONE_IN_SOURCE_NOT_OFFERED =
  'Phone enquiries are not an offered channel for this organization. Ask your administrator.';
/** Honest state when the caller's lawful clinic set is empty -- there is
 *  nothing to pick, so the save door closes instead of dead-ending. */
export const PHONE_IN_NO_CLINIC_IN_SCOPE =
  'No clinic is in your scope, so an enquiry cannot be recorded. Ask your administrator.';

/** The token this form submits. NEVER widened here: vocabulary truth stays
 *  the leads.source_normalized CHECK + LEAD_CAPTURE_SOURCES, and the route
 *  revalidates against the org's offered list. */
export const PHONE_IN_SOURCE_TOKEN = 'offline';

// ---- Pure decisions (pinned by the colocated test) -------------------------

/**
 * The workspace-header door renders on the AGENT lens ONLY. The lens is
 * SERVER-decided (GET /api/callcenter/workspace serves lens 'agent' to
 * exactly call_center_agent), and call_center_agent is in the FROZEN write
 * audience of POST /api/leads/enquiries (LEADS_WORKSPACE_ACTION_ROLES) --
 * so the door never renders for a caller the write route would refuse
 * (SEE-NOT-DO / LES-016: no dead doors). Manager-tier callers keep their
 * existing leads-workspace "Find by phone" door; this adds no authority.
 */
export function phoneInDoorVisible(lens: 'agent' | 'manager'): boolean {
  return lens === 'agent';
}

/** TRUE when the org's config-backed offer list carries the phone-in
 *  channel token (GET /api/leads/enquiry-config already intersects the
 *  configured list with the DB-lawful vocabulary server-side). */
export function offlineOffered(
  config: Pick<OperatorEnquiryConfigWire, 'sources'> | null,
): boolean {
  return config !== null && config.sources.includes(PHONE_IN_SOURCE_TOKEN);
}

export interface PhoneInFieldError {
  readonly field: 'name' | 'treatmentType' | 'clinicId';
  readonly message: string;
}

/** Client pre-validation, FIELD-SCOPED (the arrival-wizard idiom). Phone is
 *  structurally present (read-only from the searched query) and the source
 *  is fixed, so neither can fail here; the server revalidates everything. */
export function validatePhoneInForm(form: {
  readonly name: string;
  readonly treatmentType: string;
  readonly clinicId: number | null;
}): readonly PhoneInFieldError[] {
  const errors: PhoneInFieldError[] = [];
  if (form.name.trim() === '') {
    errors.push({ field: 'name', message: 'Name is required.' });
  }
  if (form.treatmentType.trim() === '') {
    errors.push({
      field: 'treatmentType',
      message: 'Pick what they asked about.',
    });
  }
  if (form.clinicId === null) {
    errors.push({ field: 'clinicId', message: 'Pick the clinic.' });
  }
  return errors;
}

function errorFor(
  errors: readonly PhoneInFieldError[],
  field: PhoneInFieldError['field'],
): string | null {
  return errors.find((e) => e.field === field)?.message ?? null;
}

/** The wizard field-error rendering (mirrors the arrival wizard's private
 *  FieldError -- same classes, same role, so the styling is the registered
 *  grammar, not a new vocabulary). */
function FieldError({ message }: { readonly message: string }): JSX.Element {
  return (
    <span className="ac-wizard__field-error" role="alert">
      <span className="ac-wizard__field-error-glyph" aria-hidden="true">
        !
      </span>
      {message}
    </span>
  );
}

function makeRequestId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `phonein-${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`;
  }
}

// ---- Pure view (react-dom/server testable; no effects, no fetch) -----------

export interface PhoneInEnquiryViewState {
  readonly config: OperatorEnquiryConfigWire | null;
  readonly query: string;
  readonly searching: boolean;
  readonly searchFailed: boolean;
  readonly result: PatientSearchResponse | null;
  readonly searchedQuery: string;
  readonly noneOfThese: boolean;
  readonly name: string;
  readonly treatmentType: string;
  readonly clinicId: number | null;
  readonly saving: boolean;
  readonly fieldErrors: readonly PhoneInFieldError[];
  /** A server failure message, VERBATIM (LES-009); null = none. */
  readonly serverError: string | null;
  readonly created: OperatorEnquiryCreatedWire | null;
}

export interface PhoneInEnquiryViewHandlers {
  readonly onQueryChange: (q: string) => void;
  readonly onSearch: () => void;
  readonly onNoneOfThese: () => void;
  readonly onNameChange: (v: string) => void;
  readonly onBucketChange: (v: string) => void;
  readonly onClinicChange: (v: number | null) => void;
  readonly onSubmit: () => void;
  readonly onLogAnother: () => void;
  readonly onOpenPatient: (card: SearchResultCardView) => void;
  readonly onOpenEnquiry: (leadId: number) => void;
  readonly onOpenCreatedLead: (safeLeadId: string) => void;
}

export function PhoneInEnquiryView({
  state,
  handlers,
  searchInFooter,
}: {
  readonly state: PhoneInEnquiryViewState;
  readonly handlers: PhoneInEnquiryViewHandlers;
  /** MY-WORK-PRIMITIVE-CONFORMANCE-V6: the modal's footer carries Search. */
  readonly searchInFooter?: boolean;
}): JSX.Element {
  const candidates: readonly SearchResultCardView[] =
    state.result === null
      ? []
      : state.result.candidates.map((c) => ({
          patientId: c.patientId,
          umr: c.umr,
          fullName: c.fullName,
          gender: c.gender,
          ageYears: c.ageYears,
          city: c.city,
          registeredClinicId: c.registeredClinicId,
          registeredClinicLabel: c.registeredClinicLabel,
        }));
  const enquiries: readonly EnquiryGlanceCard[] = state.result?.enquiries ?? [];

  // THE R-CD-R1 STRUCTURAL GATE, reused verbatim from the leads-workspace
  // door: no search evidence -> no create door; a non-phone miss explains
  // the phone-first law instead of opening it.
  const evidence = leadEnquiryEvidence({
    searched: state.result !== null || state.searchFailed,
    searchFailed: state.searchFailed,
    resultCount: candidates.length + enquiries.length,
    noneOfThese: state.noneOfThese,
    searchMode: state.result?.searchMode ?? null,
  });
  const createOpen =
    state.created === null &&
    (evidence === 'no-match' || evidence === 'none-of-these');
  const sourceOffered = offlineOffered(state.config);

  return (
    <div className="ac-wizard" data-testid="phone-in-content">
      {state.created === null ? (
        <div className="ac-wizard__card ac-wizard__search-card">
          <AcInput
            type="search"
            inputMode="tel"
            placeholder={PHONE_IN_SEARCH_PLACEHOLDER}
            aria-label={PHONE_IN_TITLE}
            data-testid="phone-in-search-input"
            value={state.query}
            onChange={(e) => handlers.onQueryChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handlers.onSearch();
            }}
          />
          {searchInFooter ? null : (
            <AcButton
              variant="primary"
              disabled={state.searching || state.query.trim().length < 2}
              onClick={handlers.onSearch}
              data-testid="phone-in-search-button"
            >
              {LEAD_SEARCH_BUTTON}
            </AcButton>
          )}
        </div>
      ) : null}

      {evidence === 'search-failed' ? (
        <p className="ac-dim" data-testid="phone-in-search-failed">
          {LEAD_SEARCH_UNAVAILABLE}
        </p>
      ) : null}

      {state.created === null && candidates.length > 0 ? (
        <ul className="ac-record-card-list" data-testid="phone-in-patients">
          {candidates.map((card) => (
            <RecordCard
              key={card.patientId}
              card={card}
              busy={state.saving}
              facts={state.result?.deskFacts?.[card.patientId] ?? null}
              onSelect={handlers.onOpenPatient}
            />
          ))}
        </ul>
      ) : null}

      {state.created === null && enquiries.length > 0 ? (
        <ul className="ac-record-card-list" data-testid="phone-in-enquiries">
          {enquiries.map((card) => (
            <li className="ac-record-card" key={card.leadId}>
              <dl className="ac-record-card__facts">
                <div className="ac-record-card__fact">
                  <dt>Name</dt>
                  <dd>{card.name ?? 'Not captured'}</dd>
                </div>
                {card.source !== '' ? (
                  <div className="ac-record-card__fact">
                    <dt>Source</dt>
                    <dd>{humanizeSource(card.source)}</dd>
                  </div>
                ) : null}
                {card.clinicName !== null ? (
                  <div className="ac-record-card__fact">
                    <dt>Clinic</dt>
                    <dd>{card.clinicName}</dd>
                  </div>
                ) : null}
              </dl>
              <AcButton
                size="sm"
                variant="primary"
                disabled={state.saving}
                onClick={() => handlers.onOpenEnquiry(card.leadId)}
                data-testid={`phone-in-open-${card.leadId}`}
              >
                {LEAD_OPEN_ENQUIRY_ACTION}
              </AcButton>
            </li>
          ))}
        </ul>
      ) : null}

      {state.created === null &&
      candidates.length + enquiries.length > 0 &&
      !state.noneOfThese ? (
        <AcButton
          variant="ghost"
          disabled={state.saving}
          onClick={handlers.onNoneOfThese}
          data-testid="phone-in-none-of-these"
        >
          {LEAD_NONE_OF_THESE}
        </AcButton>
      ) : null}

      {evidence === 'phone-first-hint' ? (
        <p className="ac-dim" data-testid="phone-in-phone-first">
          {LEAD_PHONE_FIRST_HINT}
        </p>
      ) : null}

      {createOpen ? (
        <div className="ac-wizard__card" data-testid="phone-in-create">
          {evidence === 'no-match' ? (
            <p data-testid="phone-in-no-record">
              {leadSearchNoRecordLine(state.searchedQuery)}
            </p>
          ) : null}
          <h3>{PHONE_IN_CREATE_TITLE}</h3>
          <label className="ac-wizard__field">
            <span className="ac-wizard__label">Phone</span>
            <input
              type="tel"
              value={state.searchedQuery}
              readOnly
              data-testid="phone-in-phone"
            />
          </label>
          <label className="ac-wizard__field">
            <span className="ac-wizard__label">
              Name
              <RequiredMark />
            </span>
            <input
              type="text"
              value={state.name}
              disabled={state.saving}
              aria-invalid={errorFor(state.fieldErrors, 'name') !== null}
              className={
                errorFor(state.fieldErrors, 'name') !== null
                  ? 'ac-wizard__input--error'
                  : undefined
              }
              onChange={(e) => handlers.onNameChange(e.target.value)}
              data-testid="phone-in-name"
            />
            {errorFor(state.fieldErrors, 'name') !== null ? (
              <FieldError message={errorFor(state.fieldErrors, 'name') as string} />
            ) : null}
          </label>
          {/* Pickers ride SelectorBar -- THE registered <select> owner
              (design-system conformance RULE 3: a new screen never renders
              a raw native select; the missing-control answer is a ui/
              primitive, and SelectorBar is exactly that primitive). */}
          <div className="ac-wizard__field">
            <span className="ac-wizard__label">
              What they asked about
              <RequiredMark />
            </span>
            <SelectorBar
              testId="phone-in-bucket"
              controls={[
                {
                  key: 'bucket',
                  label: 'What they asked about',
                  options: [
                    { value: '', label: 'Pick one' },
                    ...(state.config?.treatmentTypes ?? []).map((bucket) => ({
                      value: bucket,
                      label: bucket,
                    })),
                  ],
                  value: state.treatmentType,
                  onChange: handlers.onBucketChange,
                },
              ]}
            />
            {errorFor(state.fieldErrors, 'treatmentType') !== null ? (
              <FieldError
                message={errorFor(state.fieldErrors, 'treatmentType') as string}
              />
            ) : null}
          </div>
          <div className="ac-wizard__field">
            <span className="ac-wizard__label">
              Clinic
              <RequiredMark />
            </span>
            {(state.config?.clinics.length ?? 0) === 0 ? (
              <p className="ac-dim" data-testid="phone-in-no-clinic">
                {PHONE_IN_NO_CLINIC_IN_SCOPE}
              </p>
            ) : (
              <SelectorBar
                testId="phone-in-clinic"
                controls={[
                  {
                    key: 'clinic',
                    label: 'Clinic',
                    // Exactly one lawful clinic renders as SelectorBar's
                    // honest static chip (the wrapper auto-picked it);
                    // several render a real picker.
                    options:
                      (state.config?.clinics.length ?? 0) === 1
                        ? (state.config?.clinics ?? []).map((c) => ({
                            value: String(c.clinicId),
                            label: c.name,
                          }))
                        : [
                            { value: '', label: 'Pick one' },
                            ...(state.config?.clinics ?? []).map((c) => ({
                              value: String(c.clinicId),
                              label: c.name,
                            })),
                          ],
                    value: state.clinicId === null ? '' : String(state.clinicId),
                    onChange: (v) =>
                      handlers.onClinicChange(v === '' ? null : Number(v)),
                  },
                ]}
              />
            )}
            {errorFor(state.fieldErrors, 'clinicId') !== null ? (
              <FieldError
                message={errorFor(state.fieldErrors, 'clinicId') as string}
              />
            ) : null}
          </div>
          <p className="ac-dim" data-testid="phone-in-source">
            {sourceOffered ? PHONE_IN_SOURCE_LINE : PHONE_IN_SOURCE_NOT_OFFERED}
          </p>
          {state.serverError !== null ? (
            <p
              className="ac-wizard__field-error"
              role="alert"
              data-testid="phone-in-server-error"
            >
              {state.serverError}
            </p>
          ) : null}
          <AcButton
            variant="primary"
            disabled={
              state.saving ||
              state.config === null ||
              !sourceOffered ||
              (state.config?.clinics.length ?? 0) === 0
            }
            onClick={handlers.onSubmit}
            data-testid="phone-in-submit"
          >
            {PHONE_IN_SAVE_ACTION}
          </AcButton>
        </div>
      ) : null}

      {state.created !== null ? (
        <div className="ac-wizard__card" data-testid="phone-in-success">
          <p role="status">
            <strong>{PHONE_IN_SUCCESS}</strong>
          </p>
          <div className="workspace-row-actions__buttons">
            <AcButton
              variant="primary"
              onClick={handlers.onLogAnother}
              data-testid="phone-in-log-another"
            >
              {PHONE_IN_ANOTHER_ACTION}
            </AcButton>
            <AcButton
              variant="ghost"
              onClick={() =>
                handlers.onOpenCreatedLead(
                  (state.created as OperatorEnquiryCreatedWire).safeLeadId,
                )
              }
              data-testid="phone-in-open-created"
            >
              {PHONE_IN_OPEN_LEAD_ACTION}
            </AcButton>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---- The stateful modal (fetch wiring; the page renders this) --------------

export default function PhoneInEnquiryModal({
  onClose,
  visual = 'current',
}: {
  /** captured = at least one lead was saved this open (the page refreshes
   *  its queue on a captured close, never mid-success). */
  readonly onClose: (captured: boolean) => void;
  /** MY-WORK-PRIMITIVE-CONFORMANCE-V6: under the reference visual system
   *  the modal is header / body / FOOTER WITH ACTIONS -- Close is a button
   *  in the footer beside Search (the founder-approved reference), never a
   *  text link in the header. 'current' (default) renders every existing
   *  mount byte-identically. */
  readonly visual?: VisualVariant;
}): JSX.Element {
  const navigate = useNavigate();
  const [config, setConfig] = useState<OperatorEnquiryConfigWire | null>(null);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [result, setResult] = useState<PatientSearchResponse | null>(null);
  const [searchedQuery, setSearchedQuery] = useState('');
  const [noneOfThese, setNoneOfThese] = useState(false);
  const [name, setName] = useState('');
  const [treatmentType, setTreatmentType] = useState('');
  const [clinicId, setClinicId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<readonly PhoneInFieldError[]>(
    [],
  );
  const [serverError, setServerError] = useState<string | null>(null);
  const [created, setCreated] = useState<OperatorEnquiryCreatedWire | null>(
    null,
  );
  const [capturedAny, setCapturedAny] = useState(false);
  // Per-submission idempotency key (LESS-AC-02). Held in state so "Log
  // another call" mints a FRESH id -- a reused id would dedupe the next
  // capture onto the first lead.
  const [requestId, setRequestId] = useState(() => makeRequestId());

  useEffect(() => {
    let alive = true;
    fetchOperatorEnquiryConfig()
      .then((wire) => {
        if (!alive) return;
        setConfig(wire);
        if (wire.clinics.length === 1) setClinicId(wire.clinics[0]!.clinicId);
      })
      .catch(() => {
        // The save door stays disabled while config is null (honest state);
        // the server would re-refuse anyway (the real fence).
        if (alive) setConfig(null);
      });
    return () => {
      alive = false;
    };
  }, []);

  const close = useCallback(() => {
    onClose(capturedAny);
  }, [capturedAny, onClose]);

  const runSearch = useCallback(async () => {
    const q = query.trim();
    if (q.length < 2) return;
    setSearching(true);
    setSearchFailed(false);
    setNoneOfThese(false);
    setServerError(null);
    try {
      const res = await fetchPatientMasterSearch(q);
      setResult(res);
      setSearchedQuery(q);
    } catch {
      setResult(null);
      setSearchFailed(true);
      setSearchedQuery(q);
    } finally {
      setSearching(false);
    }
  }, [query]);

  const submit = useCallback(async () => {
    const errors = validatePhoneInForm({ name, treatmentType, clinicId });
    if (errors.length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors([]);
    setServerError(null);
    setSaving(true);
    try {
      const wire = await createOperatorEnquiry({
        phone: searchedQuery,
        name: name.trim(),
        treatmentType,
        source: PHONE_IN_SOURCE_TOKEN,
        clinicId: clinicId as number,
        requestId,
      });
      setCreated(wire);
      setCapturedAny(true);
    } catch (err) {
      // LES-009: the server's message verbatim -- it names the field.
      setServerError(
        err instanceof Error && err.message !== ''
          ? err.message
          : 'The enquiry could not be saved. Nothing was recorded.',
      );
    } finally {
      setSaving(false);
    }
  }, [clinicId, name, requestId, searchedQuery, treatmentType]);

  const logAnother = useCallback(() => {
    setQuery('');
    setResult(null);
    setSearchedQuery('');
    setSearchFailed(false);
    setNoneOfThese(false);
    setName('');
    setTreatmentType('');
    setClinicId(
      config !== null && config.clinics.length === 1
        ? config.clinics[0]!.clinicId
        : null,
    );
    setFieldErrors([]);
    setServerError(null);
    setCreated(null);
    setRequestId(makeRequestId());
  }, [config]);

  const state: PhoneInEnquiryViewState = useMemo(
    () => ({
      config,
      query,
      searching,
      searchFailed,
      result,
      searchedQuery,
      noneOfThese,
      name,
      treatmentType,
      clinicId,
      saving,
      fieldErrors,
      serverError,
      created,
    }),
    [
      config,
      query,
      searching,
      searchFailed,
      result,
      searchedQuery,
      noneOfThese,
      name,
      treatmentType,
      clinicId,
      saving,
      fieldErrors,
      serverError,
      created,
    ],
  );

  const handlers: PhoneInEnquiryViewHandlers = useMemo(
    () => ({
      onQueryChange: setQuery,
      onSearch: () => void runSearch(),
      onNoneOfThese: () => setNoneOfThese(true),
      onNameChange: setName,
      onBucketChange: setTreatmentType,
      onClinicChange: setClinicId,
      onSubmit: () => void submit(),
      onLogAnother: logAnother,
      onOpenPatient: (card) => {
        onClose(capturedAny);
        navigate(patientRecordHref(card.patientId));
      },
      onOpenEnquiry: (leadId) => {
        onClose(capturedAny);
        navigate(`/dashboard/leads/${leadId}`);
      },
      onOpenCreatedLead: (safeLeadId) => {
        onClose(true);
        navigate(`/dashboard/leads/${safeLeadId}`);
      },
    }),
    [capturedAny, logAnother, navigate, onClose, runSearch, submit],
  );

  const v1 = visual === 'v1';
  const footer = v1 ? (
    <>
      <AcButton variant="secondary" onClick={close} data-testid="phone-in-close">
        Close
      </AcButton>
      {state.created === null ? (
        <AcButton
          variant="primary"
          disabled={state.searching || state.query.trim().length < 2}
          onClick={handlers.onSearch}
          data-testid="phone-in-search-button"
        >
          {LEAD_SEARCH_BUTTON}
        </AcButton>
      ) : null}
    </>
  ) : undefined;
  return (
    <ModalShell
      title={PHONE_IN_TITLE}
      onClose={close}
      testId="phone-in-enquiry-modal"
      hideCloseButton={v1 ? true : undefined}
      footer={footer}
    >
      <PhoneInEnquiryView state={state} handlers={handlers} searchInFooter={v1} />
    </ModalShell>
  );
}
